import React from 'react';
import { FileText, AlertCircle, Scale, Shield, CreditCard, Search, Bell } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-lg md:text-xl opacity-90">
            Please read these terms carefully before using our services.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              By accessing the <strong>Assess Trademark</strong> website (referred to as the "Site"), you acknowledge and accept the terms and conditions outlined herein, forming an agreement between you and <strong>Assess Trademark</strong> (referred to as "we," "us," or "our"). This agreement also incorporates our Privacy Policy as if fully stated here. If you do not agree with these terms, please refrain from using the Site. We offer general information related to trademarks and provide a platform where you can prepare and file trademarks.
            </p>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-12">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-red-900 font-semibold">
                  THIS AGREEMENT INCLUDES A MANDATORY ARBITRATION CLAUSE GOVERNED BY THE FEDERAL ARBITRATION ACT, AND A WAIVER OF CLASS ACTIONS.
                </p>
              </div>
            </div>

            {/* Conditions for Use */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Conditions for Use of the Site</h2>
              <p className="text-gray-700 mb-4">
                Your access to and use of the Site is contingent upon your acceptance and adherence to these terms. By using the Site, you agree that you:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>Will comply with these Terms of Service;</li>
                <li>Are 18 years of age or older;</li>
                <li>Will not use the Site in any way that violates U.S. laws, including its export and re-export regulations;</li>
                <li>Will not copy or distribute any content from the Site without our prior written consent;</li>
                <li>Will provide accurate details when registering, submitting content, or interacting with the Site;</li>
                <li>Will not permit others to use the Site under your user credentials;</li>
                <li>Are fully responsible for all activity under your account and user credentials;</li>
                <li>Will not use the Site for commercial purposes other than those expressly stated;</li>
                <li>Will not use the Site to collect personal information about others or solicit users or advertisers;</li>
                <li>Are solely responsible for any content you submit to the Site, ensuring you have the rights or licenses to do so, and will not upload any materials infringing on others' copyrights or proprietary rights;</li>
                <li>Grant us and other users a non-exclusive license to use, display, reproduce, distribute, or create derivative works from your submitted materials; and</li>
                <li>Agree that we may remove any of your content and suspend or terminate your account, with or without prior notice.</li>
              </ul>
            </div>

            {/* Your Content */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Your Content</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to publish any content you provide (referred to as "User-Generated Content" or "UGC"). You agree not to upload or use any UGC that:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>Violates the intellectual property rights of others, including copyright, trademarks, or trade secrets;</li>
                <li>Infringes on privacy or publicity rights of any third party;</li>
                <li>Is unlawful, inaccurate, defamatory, discriminatory, obscene, or harassing;</li>
                <li>Encourages criminal activity or civil liability;</li>
                <li>Is deemed inappropriate by us, in our sole discretion; or</li>
                <li>Could harm our company, affiliates, or partners.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We are not obligated to remove UGC violating these terms, but we reserve the right to do so at our discretion, and may take further action such as account termination. We are not liable for any harm caused by UGC.
              </p>
            </div>

            {/* Your Behavior */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Your Behavior</h2>
              <p className="text-gray-700 mb-4">
                By using the Site, you agree not to:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li>Engage in or promote illegal activities;</li>
                <li>Attempt to reverse engineer or disrupt the Site's functionality;</li>
                <li>Attempt to access restricted areas of the Site;</li>
                <li>Use the Site for unsolicited email campaigns or spam;</li>
                <li>Use automated tools to scrape or collect data from the Site;</li>
                <li>Interfere with the proper functioning of the Site;</li>
                <li>Impersonate another user.</li>
              </ul>
            </div>

            {/* Third-Party Links */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Links to Third-Party Sites</h2>
              <p className="text-gray-700">
                Our Site may contain links to third-party websites not controlled by us. We assume no responsibility for the content or practices of these external sites. Linking to these sites does not imply endorsement by us.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700">
                We reserve the right to modify or discontinue the Site or any services provided without prior notice. Additionally, we may terminate this agreement for any reason, with or without notice. If we terminate your access due to violations of these terms, you will immediately lose access to the Site. The clauses related to warranties, liabilities, indemnity, dispute resolution, and your content will survive the termination of this agreement.
              </p>
            </div>

            {/* Credit Card Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Credit Card Information</h2>
              </div>
              <p className="text-gray-700">
                We may store your credit card details only for the purpose of paying the filing fees associated with your order, provided you approve the application and are aware of the costs involved.
              </p>
            </div>

            {/* Search Services */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-secondary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Search Services</h2>
              </div>
              <p className="text-gray-700 mb-4">
                If you use our search services, we employ reasonable methods to identify potential matches to the trademarks you wish to use. However, we cannot guarantee that your trademark will be approved by the USPTO or that it will not face challenges, even with our search services. There are cases where automated searches may not identify conflicts that could result in rejection by the USPTO. Similarly, for common law mark searches, while we use commercially reasonable methods to find potential matches, we cannot guarantee that all common law claims will be identified.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Trademark Search Scope</h3>
              <ul className="space-y-3 text-gray-700">
                <li><strong>Assess Trademark federal search</strong> reviews the USPTO database and includes direct matches, phonetic similarities, or visual design similarities.</li>
                <li><strong>Assess Trademark federal, state, and common law search</strong> extends to all 50 states, a business registry, and domain name databases, including matches in phonetics, design, or translation.</li>
                <li><strong>Assess Trademark global search</strong> includes the USPTO database, state databases, business registries, domain name databases, the WIPO database, and the European Community database, focusing on direct matches, phonetic similarities, or visual design likenesses.</li>
              </ul>
            </div>

            {/* Trademark Monitoring */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Bell className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Trademark Monitoring Services</h2>
              </div>
              <p className="text-gray-700">
                Our trademark monitoring service reviews the filing of new applications that are direct matches, phonetically similar, similar in terms of translation, or appearance by way of design to your marks as published in the Official Gazette of the USPTO and Assess Trademark.
              </p>
            </div>

            {/* Subscription Terms */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Subscription and Auto-Renewals</h2>
              <p className="text-gray-700 mb-4">
                If you signed up for one or more subscription services through the Website, these additional terms apply. Your license to the Services is valid during the period your subscription remains in good standing and there are no outstanding subscription payments (hereinafter "Subscription Term"). To keep your Subscription Term valid and in force, you must pay all charges to your account relating to your subscriptions, including applicable taxes and fees. Unless otherwise provided, your Subscription Term is on an automatic renewal and you will be required to pay, through the credit card provided by you, for the next applicable Subscription Term at the beginning of the Subscription Term period. These charges are applied regardless of whether you continue to use the Services during the Subscription Term.
              </p>
              <p className="text-gray-700 font-semibold mb-4">
                YOU ARE RESPONSIBLE FOR SUBSCRIPTION FEES UNTIL YOU CANCEL YOUR SUBSCRIPTION OR IT IS OTHERWISE TERMINATED.
              </p>
              <p className="text-gray-700 mb-4">
                If your Subscription Term is month to month and your purchase date is on the 29th through 31st day of any month, your renewal date will fall on the last day of the month for any succeeding months with fewer days. Unless otherwise provided, the renewal period will be the same as the price you originally paid.
              </p>
              <p className="text-gray-700 mb-4">
                We may offer promotional trial memberships. Although the promotional trial membership may be advertised as free promotional memberships, you authorize us to charge your credit card $1 to confirm the source of the payment. <strong>AT THE END OF A TRIAL OR PROMOTIONAL MEMBERSHIP, UNLESS OTHERWISE STATED IN THE OFFER OR YOU CANCEL, YOUR SUBSCRIPTION WILL RENEW AUTOMATICALLY AND YOUR CREDIT CARD WILL BE CHARGED TO BEGIN YOUR INITIAL SUBSCRIPTION TERM.</strong> It is very important to understand that you will not receive a notice from us that your free trial has ended and that payment for your subscription is due.
              </p>
            </div>

            {/* Disclaimers */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Disclaimers of Warranty and Limitations of Liability</h2>
              </div>
              <p className="text-gray-700 mb-4">
                We provide the Website and the related services "as is", "where is", and "as available." We make no express and/or implied warranties or guarantees about the Website, the goods and services described thereon or the Advertisers.
              </p>
              <p className="text-gray-700 font-semibold mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE AND OUR OFFICERS, DIRECTORS, AGENTS, VENDORS, AND THE MERCHANTS WHO ADVERTISE WITH US DISCLAIM ANY AND ALL EXPRESS OR IMPLIED WARRANTIES THAT THE WEBSITE AND SERVICES ARE MERCHANTABLE, OF SATISFACTORY QUALITY, ACCURATE, TIMELY OR FIT FOR A PARTICULAR PURPOSE OR NEED. WE IN NO WAY GUARANTEE THAT WE WILL MEET YOUR REQUIREMENTS, IS ERROR-FREE, ACCURATE, RELIABLE, WITHOUT INTERRUPTION OR AVAILABLE AT ALL TIMES.
              </p>
              <p className="text-gray-700">
                YOUR SOLE AND EXCLUSIVE REMEDY FOR ANY DISPUTE OR CLAIM WITH US IS TO CEASE USE OF THE WEBSITE. WE, OUR AFFILIATES, OFFICERS, DIRECTORS, AGENTS, VENDORS, AND MERCHANTS WHO ADVERTISE HERE, SHALL NOT BE LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES ARISING FROM YOUR USE OF, INABILITY TO USE, OR RELIANCE UPON THE WEBSITE IN ANY WAY.
              </p>
            </div>

            {/* Arbitration */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Dispute Resolution & Arbitration</h2>
              </div>
              <p className="text-gray-700 mb-4">
                For the purposes of this Section, references to "Assess Trademark," "you," and "us" include our respective subsidiaries, affiliates, agents, employees, business partners, predecessors in interest, successors, and assigns, as well as all authorized or unauthorized users or beneficiaries of services or products under these Terms or any prior agreements between us.
              </p>
              <p className="text-gray-700 mb-4">
                Any Dispute shall be FINALLY and EXCLUSIVELY resolved by binding individual arbitration conducted by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. This arbitration agreement is made pursuant to a transaction involving interstate commerce, and shall be governed by the Federal Arbitration Act (9 U.S.C. 1-16).
              </p>
              <p className="text-gray-700 font-semibold mb-4">
                ARBITRATION OF YOUR CLAIM IS MANDATORY AND BINDING. NEITHER PARTY WILL HAVE THE RIGHT TO LITIGATE THAT CLAIM THROUGH A COURT. IN ARBITRATION, NEITHER PARTY WILL HAVE THE RIGHT TO A JURY TRIAL OR TO ENGAGE IN DISCOVERY, EXCEPT AS PROVIDED FOR IN THE AAA CODE OF PROCEDURE.
              </p>
              <p className="text-gray-700 font-semibold mb-4">
                NO CLASS ACTION, OR OTHER REPRESENTATIVE ACTION OR PRIVATE ATTORNEY GENERAL ACTION OR JOINDER OR CONSOLIDATION OF ANY CLAIM WITH A CLAIM OF ANOTHER PERSON OR CLASS OF CLAIMANTS SHALL BE ALLOWABLE.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">RIGHT TO OPT-OUT</h3>
              <p className="text-gray-700">
                You may opt-out of arbitration by notifying us of your decision to reject the arbitration clause in writing. You must notify us in writing within 30 days of accepting this Agreement. Failure to do so will make this Agreement binding on you.
              </p>
            </div>

            {/* Refund Policy */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Refund Policy</h2>
              </div>
              <p className="text-gray-700">
                All packages include attorney-conducted searches and are non-refundable. Once a search and clearance report is issued, payment will be automatically processed within 24-48 hours if we do not receive a response from you via email or phone. The payment will be transferred to the USPTO (United States Patent and Trademark Office) through the attorney.
              </p>
            </div>

            {/* Modifications */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Modifications to the Agreement</h2>
              <p className="text-gray-700">
                We retain the right to modify or discontinue any of these terms and/or the Privacy Policy at any time, as we deem necessary. Changes may include the introduction of new fees or charges. We may, but are not required to, notify you of significant changes through email or by posting a notice on the Website. Any modifications will be effective when we send an email notice to you or post the changes on the Website, whichever comes first. We recommend periodically reviewing this section of the Website to stay updated on any modifications. Continued use of the Website after such notice shall be deemed as acceptance of the changes.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-primary/10 p-8 rounded-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Questions?</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms & Conditions, please contact us.
              </p>
              <p className="text-gray-600 italic">
                Last Updated: 2025
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
